import React from "react";
import LandingHeader from "../components/layout/LandingHeader";
import LandingFooter from "../components/layout/LandingFooter";
import WhatsAppButton from "../components/common/WhatsAppButton";
import { Button } from "../components/ui";
import {
  ArrowRight,
  MessageCircle,
  Calendar,
  Ticket,
  CreditCard,
  QrCode,
  BarChart2,
  Sliders,
  XCircle,
  X,
  CheckCircle2,
  Check,
  ArrowUp,
} from "lucide-react";

function ParaOrganizadores() {
  return (
    <div className="min-h-screen flex flex-col bg-urb-dark text-slate-100 selection:bg-urb-primary selection:text-black">
      <LandingHeader />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-12 px-4 md:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 items-center gap-10">
          <div className="flex flex-col gap-4">
            <span className="font-semibold text-xs sm:text-sm tracking-wider uppercase text-urb-primary">
              PARA ORGANIZADORES
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black m-0 leading-tight">
              ORGANIZE EVENTOS INCRÍVEIS. <br className="hidden md:inline" />
              <span className="text-urb-primary drop-shadow-[0_0_12px_rgba(138,230,0,0.3)]">
                NÓS CUIDAMOS DO RESTO.
              </span>
            </h1>
            <p className="text-slate-400 text-base sm:text-lg m-0 leading-relaxed max-w-lg">
              A plataforma completa para você planejar, gerir e entregar
              experiências inesquecíveis do início ao fim.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-3">
              <Button
                variant="primary"
                size="lg"
                className="font-bold text-sm uppercase px-6 py-3.5 flex items-center justify-center gap-2"
              >
                COMEÇAR AGORA
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm bg-emerald-600 hover:bg-emerald-500 text-white transition-all shadow-lg cursor-pointer"
                onClick={() =>
                  window.open(
                    "https://wa.me/5596981004733",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <MessageCircle className="w-4 h-4" />
                FALAR COM ESPECIALISTA
              </button>
            </div>
          </div>

          {/* Hero Graphic & Stats Panel */}
          <div className="flex justify-center relative mt-6 lg:mt-0">
            {/* Glow BG */}
            <div className="absolute w-72 h-72 rounded-full blur-3xl opacity-20 bg-urb-primary pointer-events-none" />

            <div className="relative z-10 flex flex-col gap-4 w-full max-w-md p-6 rounded-2xl border border-urb-border bg-urb-card/90 shadow-2xl backdrop-blur-sm">
              <div className="flex items-center justify-between pb-3 border-b border-urb-border">
                <span className="font-extrabold text-lg text-white">
                  URB LIVE <span className="text-urb-primary">STATUS</span>
                </span>
                <span className="text-xs px-2.5 py-0.5 rounded-full font-bold text-urb-primary bg-urb-primary/10 border border-urb-primary/30">
                  ON
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1 p-3 rounded-xl bg-slate-950/70 border border-slate-800">
                  <span className="text-[10px] text-slate-400 font-semibold tracking-wider">
                    EVENTOS REALIZADOS
                  </span>
                  <span className="text-2xl font-black text-white">+300</span>
                </div>
                <div className="flex flex-col gap-1 p-3 rounded-xl bg-slate-950/70 border border-slate-800">
                  <span className="text-[10px] text-slate-400 font-semibold tracking-wider">
                    ATLETAS IMPACTADOS
                  </span>
                  <span className="text-2xl font-black text-white">+50 mil</span>
                </div>
                <div className="flex flex-col gap-1 p-3 rounded-xl bg-slate-950/70 border border-slate-800">
                  <span className="text-[10px] text-slate-400 font-semibold tracking-wider">
                    SUPORTE DEDICADO
                  </span>
                  <span className="text-2xl font-black text-white">100%</span>
                </div>
                <div className="flex flex-col gap-1 p-3 rounded-xl bg-slate-950/70 border border-slate-800">
                  <span className="text-[10px] text-slate-400 font-semibold tracking-wider">
                    PAGAMENTOS
                  </span>
                  <span className="text-2xl font-black text-urb-primary">
                    R$ 98M+
                  </span>
                </div>
              </div>

              {/* Graphic of runner */}
              <div
                className="h-32 rounded-xl flex items-center justify-center p-3 relative overflow-hidden border border-slate-800"
                style={{
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.2)), url('https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&w=600&q=80') center/cover",
                }}
              >
                <div className="text-center z-10">
                  <div className="text-xs text-slate-400 font-mono tracking-widest">
                    LIVE TIME
                  </div>
                  <div className="text-2xl font-mono text-urb-primary font-black drop-shadow">
                    00:34:21
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Tudo que você precisa */}
        <section className="py-16 px-4 md:px-8 border-y border-urb-border bg-slate-950/40">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-black text-white">
                TUDO QUE VOCÊ PRECISA{" "}
                <span className="text-urb-primary">EM UM SÓ LUGAR</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "GESTÃO COMPLETA", text: "Do planejamento à entrega do evento.", icon: Calendar },
                { title: "INSCRIÇÕES FLEXÍVEIS", text: "Lotes, categorias, cupons e formulários.", icon: Ticket },
                { title: "PAGAMENTOS SEGUROS", text: "Recebimentos online, split e repasse automático.", icon: CreditCard },
                { title: "CHECK-IN INTELIGENTE", text: "QR Code, offline e controle de presença.", icon: QrCode },
                { title: "RESULTADOS E RANKINGS", text: "Em tempo real, com certificados e pódios personalizados.", icon: BarChart2 },
                { title: "RELATÓRIOS AVANÇADOS", text: "Dados estratégicos para decisões inteligentes.", icon: Sliders },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 rounded-2xl bg-urb-card/70 border border-urb-border hover:border-urb-primary/50 transition-all flex flex-col gap-3 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-urb-primary" />
                    </div>
                    <h3 className="text-white font-bold text-base m-0">{item.title}</h3>
                    <p className="text-slate-400 text-xs sm:text-sm m-0 leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Section: Desafios vs Solução */}
        <section className="py-16 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative items-center">
            {/* Desafios Card */}
            <div className="flex flex-col gap-5 p-8 rounded-3xl bg-red-950/20 border border-red-900/40">
              <h3 className="text-red-400 font-extrabold text-xl m-0 flex items-center gap-2">
                <XCircle className="w-6 h-6 text-red-400" />
                OS DESAFIOS DE ORGANIZAR EVENTOS
              </h3>
              <ul className="flex flex-col gap-3 p-0 m-0 list-none text-sm text-slate-300">
                {[
                  "Planilhas e processos manuais",
                  "Inscrições descentralizadas",
                  "Falta de controle financeiro",
                  "Comunicação ineficiente",
                  "Erros, filas e reclamações",
                  "Relatórios demorados",
                  "Risco de fraudes",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <X className="w-4 h-4 text-red-400 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Soluções Card */}
            <div className="flex flex-col gap-5 p-8 rounded-3xl bg-urb-primary/10 border border-urb-primary/40 shadow-xl">
              <h3 className="text-urb-primary font-extrabold text-xl m-0 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-urb-primary" />
                A SOLUÇÃO É A URB RACE
              </h3>
              <ul className="flex flex-col gap-3 p-0 m-0 list-none text-sm text-white">
                {[
                  "Automação total da gestão",
                  "Inscrições rápidas e seguras",
                  "Financeiro transparente e integrado",
                  "Comunicação automática",
                  "Check-in ágil e sem filas",
                  "Resultados instantâneos",
                  "Segurança e confiança",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-urb-primary shrink-0 stroke-[3]" />
                    <span className="font-semibold">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Controle Total */}
        <section className="py-16 px-4 md:px-8 border-t border-urb-border bg-slate-950/20">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 items-center gap-10">
            <div className="flex flex-col gap-4">
              <span className="font-bold text-xs uppercase text-urb-primary tracking-wider">
                PLATAFORMA COMPLETA
              </span>
              <h2 className="text-3xl md:text-4xl font-black m-0 leading-tight text-white">
                CONTROLE TOTAL <br />
                <span className="text-urb-primary">NA PALMA DA MÃO</span>
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed max-w-lg m-0">
                Tenha uma visão 360° do seu evento com nossos dashboards intuitivos e atualizações em tempo real.
              </p>
              <div className="flex flex-col gap-3 mt-2">
                {[
                  "Acompanhe inscrições e vendas em tempo real",
                  "Monitore pagamentos e repasses automáticos",
                  "Gerencie equipes e controle permissões de staff",
                  "Analise dados com relatórios inteligentes e tome decisões",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-urb-primary" />
                    </div>
                    <span className="text-slate-200 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mockup Dashboard Card */}
            <div className="flex justify-center mt-4 lg:mt-0">
              <div className="w-full max-w-xl p-6 rounded-2xl bg-urb-card border border-urb-border shadow-2xl flex flex-col gap-4">
                <div className="flex items-center justify-between border-b border-urb-border pb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] font-black bg-urb-primary text-black px-2 py-0.5 rounded">
                      URB
                    </div>
                    <span className="text-xs text-white font-bold">Dashboard</span>
                  </div>
                  <span className="text-xs text-slate-400">Visão Geral</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/80">
                    <span className="text-[10px] text-slate-400 font-semibold block mb-1">
                      INSCRITOS
                    </span>
                    <span className="text-2xl font-bold text-white block">2.431</span>
                    <span className="text-[10px] text-urb-primary flex items-center gap-1 mt-1 font-semibold">
                      <ArrowUp className="w-3 h-3" /> +15% vs anterior
                    </span>
                  </div>
                  <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/80">
                    <span className="text-[10px] text-slate-400 font-semibold block mb-1">
                      RECEITA
                    </span>
                    <span className="text-xl font-bold text-white block">R$ 98.670</span>
                    <span className="text-[10px] text-urb-primary flex items-center gap-1 mt-1 font-semibold">
                      <ArrowUp className="w-3 h-3" /> +18% vs anterior
                    </span>
                  </div>
                  <div className="p-3.5 rounded-xl border border-slate-800 bg-slate-950/80">
                    <span className="text-[10px] text-slate-400 font-semibold block mb-1">
                      CHECK-INS
                    </span>
                    <span className="text-2xl font-bold text-white block">1.987</span>
                    <span className="text-[10px] text-slate-400 block mt-1">9% restante</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-2">
                  <span className="text-xs text-slate-400 font-semibold">
                    INSCRIÇÕES AO LONGO DO TEMPO
                  </span>
                  <div className="h-20 flex items-end justify-between px-2 pt-4 gap-2">
                    <div className="flex-1 bg-urb-primary/30 rounded-t" style={{ height: "30%" }} />
                    <div className="flex-1 bg-urb-primary/50 rounded-t" style={{ height: "45%" }} />
                    <div className="flex-1 bg-urb-primary/70 rounded-t" style={{ height: "60%" }} />
                    <div className="flex-1 bg-urb-primary/85 rounded-t" style={{ height: "80%" }} />
                    <div className="flex-1 bg-urb-primary rounded-t shadow-[0_0_10px_rgba(138,230,0,0.5)]" style={{ height: "95%" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-8 px-4 md:px-8 max-w-5xl mx-auto my-10">
          <div className="bg-gradient-to-r from-slate-900 via-urb-card to-slate-900 border border-urb-border rounded-3xl p-6 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl relative overflow-hidden">
            <div className="flex flex-col gap-3 relative z-10 text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                PRONTO PARA ELEVAR SEUS EVENTOS <br />
                <span className="text-urb-primary">AO PRÓXIMO NÍVEL?</span>
              </h2>
              <p className="m-0 text-slate-300 text-sm max-w-lg leading-relaxed">
                Junte-se a centenas de organizadores que já confiam na URB RACE para criar experiências inesquecíveis.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 relative z-10 w-full md:w-auto">
              <Button
                variant="primary"
                size="lg"
                className="font-black text-sm uppercase px-6 py-3.5 tracking-wider shadow-lg flex items-center justify-center gap-2"
              >
                CRIAR MEU EVENTO
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm bg-emerald-600 hover:bg-emerald-500 text-white transition-all shadow-lg cursor-pointer"
                onClick={() =>
                  window.open(
                    "https://wa.me/5596981004733",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <MessageCircle className="w-4 h-4" />
                FALAR COM ESPECIALISTA
              </button>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />

      {/* Botão Flutuante do WhatsApp */}
      <WhatsAppButton />
    </div>
  );
}

export default ParaOrganizadores;
